/**
 * Detect Visible Bounds - Client-side transparency trimming detection
 *
 * This utility analyzes an image to find the bounding box of non-transparent pixels.
 * Used to display the correct selection frame BEFORE the server-side cropping completes.
 *
 * @see docs/features/FEATURE_ASSET_PROCESSING.md
 */

export interface VisibleBounds {
  /** X offset from left edge to first visible pixel */
  x: number;
  /** Y offset from top edge to first visible pixel */
  y: number;
  /** Width of visible content */
  width: number;
  /** Height of visible content */
  height: number;
  /** Original image width */
  originalWidth: number;
  /** Original image height */
  originalHeight: number;
  /** Whether any trimming would occur */
  wasTrimmed: boolean;
}

/**
 * Alpha threshold for considering a pixel as "visible"
 * Matches the server-side TRIM_THRESHOLD in process-asset.ts
 */
const ALPHA_THRESHOLD = 10;

/**
 * Detect the visible (non-transparent) bounds of an image
 *
 * @param imageSource - Image URL (blob:// or http://) or HTMLImageElement
 * @returns Promise with the visible bounds information
 */
export async function detectVisibleBounds(
  imageSource: string | HTMLImageElement
): Promise<VisibleBounds> {
  // Load image if string URL provided
  const img =
    typeof imageSource === 'string'
      ? await loadImage(imageSource)
      : imageSource;

  const { width, height } = img;

  // Create off-screen canvas to read pixel data
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  // Draw image to canvas
  ctx.drawImage(img, 0, 0);

  // Get pixel data (RGBA format)
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  // Find bounds of non-transparent pixels
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let hasVisiblePixels = false;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Alpha channel is every 4th byte (RGBA)
      const alpha = pixels[(y * width + x) * 4 + 3] ?? 0;

      if (alpha > ALPHA_THRESHOLD) {
        hasVisiblePixels = true;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  // Handle fully transparent images
  if (!hasVisiblePixels) {
    return {
      x: 0,
      y: 0,
      width,
      height,
      originalWidth: width,
      originalHeight: height,
      wasTrimmed: false,
    };
  }

  // Calculate visible dimensions (+1 because max is inclusive)
  const visibleWidth = maxX - minX + 1;
  const visibleHeight = maxY - minY + 1;

  // Check if any trimming would occur
  const wasTrimmed =
    minX > 0 || minY > 0 || visibleWidth < width || visibleHeight < height;

  return {
    x: minX,
    y: minY,
    width: visibleWidth,
    height: visibleHeight,
    originalWidth: width,
    originalHeight: height,
    wasTrimmed,
  };
}

/**
 * Load an image from URL and wait for it to be ready
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));

    img.src = src;
  });
}

/**
 * Quick check if an image has any transparency
 * Faster than full bounds detection for images without alpha channel
 */
export async function hasTransparency(
  imageSource: string | HTMLImageElement
): Promise<boolean> {
  const img =
    typeof imageSource === 'string'
      ? await loadImage(imageSource)
      : imageSource;

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, img.width, img.height);
  const pixels = imageData.data;

  // Check alpha channel of every 100th pixel (fast sampling)
  for (let i = 3; i < pixels.length; i += 400) {
    if ((pixels[i] ?? 255) < 255) {
      return true;
    }
  }

  return false;
}

/**
 * Result of cropping an image to its visible bounds
 */
export interface CroppedImageResult {
  /** Blob URL of the cropped image */
  blobUrl: string;
  /** The visible bounds information */
  bounds: VisibleBounds;
}

/**
 * Crop an image to its visible (non-transparent) bounds
 *
 * This creates a new image containing only the visible pixels,
 * avoiding Fabric.js issues with cropX/cropY/width/height properties.
 *
 * @param imageSource - Image URL (blob:// or http://) or HTMLImageElement
 * @returns Promise with the cropped image blob URL and bounds info
 */
export async function cropImageToVisibleBounds(
  imageSource: string | HTMLImageElement
): Promise<CroppedImageResult> {
  // Load image if string URL provided
  const img =
    typeof imageSource === 'string'
      ? await loadImage(imageSource)
      : imageSource;

  // Detect visible bounds
  const bounds = await detectVisibleBounds(img);

  // If no trimming needed, return original
  if (!bounds.wasTrimmed) {
    const blobUrl =
      typeof imageSource === 'string'
        ? imageSource
        : await imageElementToBlobUrl(img);

    return { blobUrl, bounds };
  }

  // Create canvas with cropped dimensions
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = bounds.width;
  croppedCanvas.height = bounds.height;

  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context for cropping');
  }

  // Draw only the visible portion
  ctx.drawImage(
    img,
    bounds.x, // Source X
    bounds.y, // Source Y
    bounds.width, // Source width
    bounds.height, // Source height
    0, // Destination X
    0, // Destination Y
    bounds.width, // Destination width
    bounds.height // Destination height
  );

  // Convert to blob URL
  const blobUrl = await canvasToBlobUrl(croppedCanvas);

  return { blobUrl, bounds };
}

/**
 * Convert a canvas element to a blob URL
 */
function canvasToBlobUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Convert an HTMLImageElement to a blob URL
 */
async function imageElementToBlobUrl(img: HTMLImageElement): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2D context');
  }

  ctx.drawImage(img, 0, 0);
  return canvasToBlobUrl(canvas);
}

/**
 * A copy of the image no larger than `maxEdge` on its longest side, as a
 * new blob URL — or the same URL when it already fits. Drawn in one canvas
 * that is released at once: on iOS the canvas budget is per page.
 */
export async function downscaleBlobUrl(
  blobUrl: string,
  maxEdge: number
): Promise<string> {
  const img = await loadImage(blobUrl);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  if (!longest || longest <= maxEdge) return blobUrl;
  const scale = maxEdge / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return blobUrl;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  );
  canvas.width = 0;
  canvas.height = 0;
  return blob ? URL.createObjectURL(blob) : blobUrl;
}
